import { useAuthorizationStore } from '@/stores/authorization';
import { useContractStore } from '@/stores/contract';
import { useDocumentStore } from '@/stores/document';
import { useParticipantStore } from '@/stores/participant';
import { ContractStatus } from '@/types/contract';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useRoute } from 'vue-router';

export const useContract = () => {
  const route = useRoute();

const { isAuthor } = storeToRefs(useParticipantStore());

  const { getDocumentsByContractId } = storeToRefs(useDocumentStore());

  const { getContractById } = storeToRefs(useContractStore());

  const { user } = storeToRefs(useAuthorizationStore());

  const contractId = computed(
    () => (route.params?.contractId as string) ?? null,
  );

  const contract = computed(() => getContractById.value(contractId.value));

  const isContractSent = computed(() =>
    [
      ContractStatus.Sent,
      ContractStatus.Finished,
      ContractStatus.Failed,
    ].includes(contract.value.status),
  );

  const isContractArchiveable = computed(
    () =>
      [ContractStatus.Finished, ContractStatus.Failed].includes(
        contract.value.status,
      ) && +(contract.value?.folder_id || 0) !== 1,
  );

  const isContractRestoreable = computed(
    () => +(contract.value?.folder_id || 0) === 1,
  );

  const isContractFinished = computed(
    () => contract.value.status === ContractStatus.Finished,
  );

  const isContractFailed = computed(
    () => contract.value.status === ContractStatus.Failed,
  );

  // True when no participant has signed any document on the contract.
  const hasNoSignatures = computed(() => {
    const documents = getDocumentsByContractId.value(contractId.value);

    const anySigned = documents.some((document) =>
      document.participants.some(
        (participant) => participant.signature !== null,
      ),
    );

    return !anySigned;
  });

  // A sent agreement may be deleted only when the current user is the owner,
  // the contract has been terminated, and it has no signatures at all.
  const canDeleteSentContract = computed(
    () => isAuthor.value && isContractFailed.value && hasNoSignatures.value,
  );

  const isCompanyUser = computed(() => +(user.value?.is_company || 0) === 1);

  return {
    contract,
    contractId,
    isAuthor,
    isContractFailed,
    isContractFinished,
    isContractSent,
    isCompanyUser,
    isContractArchiveable,
    isContractRestoreable,
    canDeleteSentContract,
  };
};
